import { libraryTitle } from 'src/constants/teethPhotoShots';

/**
 * Pure helpers for chatbot teeth-photo items in a lead's chat transcript.
 * The transcript stores photo ids only; a fresh signed URL is fetched per photo.
 * Transcript JSON is untrusted: ids are validated before they reach an API path.
 */
export const ANGLE_LABELS = {
  front: 'Smile straight on',
  left: 'Smile turned left',
  right: 'Smile turned right',
  // Legacy angles: kept so older transcripts still caption correctly.
  upper: 'Top teeth',
  lower: 'Bottom teeth',
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const POSITIVE_INT_RE = /^[1-9]\d*$/;

export const isUuid = (value) => typeof value === 'string' && UUID_RE.test(value);
const isPositiveInt = (value) => POSITIVE_INT_RE.test(String(value ?? ''));

export const isKnownAngle = (angle) => Object.prototype.hasOwnProperty.call(ANGLE_LABELS, angle);

export const angleLabel = (angle) => (isKnownAngle(angle) ? ANGLE_LABELS[angle] : 'Photo');

const MAX_TITLE = 60;
const isLibraryAngle = (angle) => typeof angle === 'string' && libraryTitle(angle) !== null;

/** Plain-text caption from untrusted transcript content.title: tags stripped, trimmed, <=60 chars; '' when unusable. */
export const sanitizeTitle = (title) =>
  typeof title === 'string' ? title.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim().slice(0, MAX_TITLE).trim() : '';

/** Caption order: content.title -> legacy ANGLE_LABELS -> library default title -> "Photo". */
export const photoCaption = ({ angle, title } = {}) =>
  sanitizeTitle(title) || (isKnownAngle(angle) ? ANGLE_LABELS[angle] : null) || (isLibraryAngle(angle) ? libraryTitle(angle) : null) || 'Photo';

/** A photo transcript item with a well-formed UUID photo_id (anything else is rendered as unavailable). */
export const isPhotoItem = (item) => item?.message?.type === 'photo' && isUuid(item?.message?.content?.photo_id);

/** Ordered list of {photoId, angle} for every valid photo item in the transcript. */
export const collectPhotos = (transcript) =>
  (Array.isArray(transcript) ? transcript : [])
    .filter(isPhotoItem)
    .map((item) => {
      const { angle, title } = item.message.content;
      const known = isKnownAngle(angle) || isLibraryAngle(angle);
      return {
        photoId: item.message.content.photo_id,
        angle: known ? angle : undefined,
        title: sanitizeTitle(title) || undefined,
      };
    });

/** API path for a chat photo, or null when any id is not safe to interpolate. */
export const chatPhotoPath = (practiceId, leadId, photoId) => {
  if (!isPositiveInt(practiceId) || !isPositiveInt(leadId) || !isUuid(photoId)) return null;
  return `/api/v1/practices/${encodeURIComponent(practiceId)}/leads/${encodeURIComponent(leadId)}/chat-photos/${encodeURIComponent(photoId)}/`;
};
